import React, { useState, useEffect, useRef } from 'react';
import {
  initializeNotifications,
  sendTaskAssignedNotification,
  showLocalNotification,
  requestNotificationPermission,
  isPushSupported,
  isIOS
} from './utils/notificationUtils';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
  ImageList,
  ImageListItem,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Image as ImageIcon,
  PhotoCamera as PhotoCameraIcon,
  InsertDriveFile as FileIcon,
  AttachFile as AttachFileIcon,
  ScreenshotMonitor as ScreenshotIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';

// A component to display the attachment with zoom capabilities
const AttachmentViewer = ({ attachmentUrl }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  
  if (!attachmentUrl) return null;
  
  // Determine file type from URL
  // Handle both blob URLs and server paths
  const isBlob = attachmentUrl.startsWith('blob:');
  const isServerPath = attachmentUrl.startsWith('/uploads/');
  const isFullUrl = attachmentUrl.startsWith('http://') || attachmentUrl.startsWith('https://');
  
  // Get the file extension for type detection
  let fileExtension = '';
  if (isBlob) {
    // For blob URLs, assume it's an image since it came from our uploader
    fileExtension = '.png';
  } else if (isServerPath || isFullUrl) {
    fileExtension = attachmentUrl.split('.').pop().toLowerCase();
  }
  
  // Determine file type
  const isImage = isBlob || /\.(jpeg|jpg|png|gif|bmp|webp|jfif)$/i.test(attachmentUrl);
  const isPdf = /\.pdf$/i.test(attachmentUrl);
  const isDoc = /\.(doc|docx)$/i.test(attachmentUrl);
  const isSpreadsheet = /\.(xls|xlsx|csv)$/i.test(attachmentUrl);
  const isPresentation = /\.(ppt|pptx)$/i.test(attachmentUrl);
  const isHtml = /\.(html|htm)$/i.test(attachmentUrl);
  const isVideo = /\.(mp4|webm|mov)$/i.test(attachmentUrl);
  
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };
  
  const handleResetZoom = () => {
    setZoomLevel(1);
  };
  
  // Get the full URL for the attachment
  const getFullUrl = () => {
    console.log('[DEBUG] Processing attachment URL:', attachmentUrl);
    
    // Case 1: Blob URLs - return as is (for browser display)
    if (isBlob) {
      console.log('[DEBUG] Blob URL - using as is');
      return attachmentUrl;
    }
    
    // Case 2: Full URLs (http/https) - return as is
    if (isFullUrl) {
      console.log('[DEBUG] Full URL - using as is');
      return attachmentUrl;
    }
    
    // Case 3: Server paths - ALWAYS add the origin
    const baseUrl = window.location.origin;
    
    // If it's a server path starting with /uploads/
    if (attachmentUrl.startsWith('/uploads/')) {
      const fullUrl = `${baseUrl}${attachmentUrl}`;
      console.log('[DEBUG] Server path with /uploads/ - adding origin:', fullUrl);
      return fullUrl;
    }
    
    // Case 4: If it's a filename or other path without /uploads/ prefix
    // Make sure to add both the origin and /uploads/ path
    const filename = attachmentUrl.split('/').pop().split('\\').pop();
    const uploadPath = `${baseUrl}/uploads/${filename}`;
    console.log('[DEBUG] Adding both origin and /uploads/ prefix:', uploadPath);
    return uploadPath;
  };

  // Handle opening attachment directly
  const handleOpenAttachment = () => {
    const fullUrl = getFullUrl();
    // Open in a new window directly
    const newWindow = window.open();
    if (newWindow) {
      newWindow.location.href = fullUrl;
    } else {
      alert("Pop-up blocker may have prevented opening the attachment. Please allow pop-ups for this site.");
    }
  };
  
  return (
    <Card sx={{ 
      mb: 2, 
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 2 
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        p: 1, 
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
          {isImage ? 'Image' : isPdf ? 'PDF' : isDoc ? 'Document' : 
           isSpreadsheet ? 'Spreadsheet' : isPresentation ? 'Presentation' : 
           isHtml ? 'HTML' : isVideo ? 'Video' : 'File'}
        </Typography>
        <Box>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <span style={{ fontSize: '20px' }}>-</span>
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Zoom">
            <IconButton onClick={handleResetZoom} size="small">
              <span style={{ fontSize: '14px' }}>{Math.round(zoomLevel * 100)}%</span>
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <span style={{ fontSize: '20px' }}>+</span>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        p: 2,
        height: '100%',
        minHeight: '500px',
        maxHeight: '600px',
        position: 'relative'
      }}>
        {isImage ? (
          <Box sx={{ 
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto'
          }}>
            <Box sx={{
              position: 'relative',
              width: 'fit-content',
              height: 'fit-content',
              transform: `scale(${zoomLevel})`,
              transition: 'transform 0.2s',
              transformOrigin: 'center'
            }}>
              {/* Add error handling and fallbacks for image loading */}
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <img
                  src={getFullUrl()}
                  alt="Attachment"
                  style={{ 
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto'
                  }}
                  onError={(e) => {
                    console.error("Image failed to load:", e.target.src);
                    
                    // Try alternative URL formats if the primary one fails
                    const baseUrl = window.location.origin;
                    const filename = attachmentUrl.split('/').pop().split('\\').pop();
                    
                    // Try direct filename in uploads directory
                    const fallbackUrl = `${baseUrl}/uploads/${filename}`;
                    console.log("Trying fallback URL:", fallbackUrl);
                    
                    if (e.target.src !== fallbackUrl) {
                      e.target.src = fallbackUrl;
                    } else {
                      // If fallback also fails, show error message
                      e.target.style.display = 'none';
                      const errorMsg = document.createElement('div');
                      errorMsg.innerHTML = `
                        <div style="color: red; margin-top: 20px; text-align: center;">
                          <p>Image failed to load</p>
                          <p>Path: ${attachmentUrl}</p>
                          <p>Tried: ${getFullUrl()}, ${fallbackUrl}</p>
                        </div>
                      `;
                      e.target.parentNode.appendChild(errorMsg);
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            p: 3,
            textAlign: 'center'
          }}>
            <Box sx={{ mb: 2 }}>
              {isPdf && <FileIcon fontSize="large" color="error" />}
              {isDoc && <FileIcon fontSize="large" color="primary" />}
              {isSpreadsheet && <FileIcon fontSize="large" color="success" />}
              {isPresentation && <FileIcon fontSize="large" color="warning" />}
              {isHtml && <FileIcon fontSize="large" color="info" />}
              {isVideo && <FileIcon fontSize="large" color="secondary" />}
              {!isPdf && !isDoc && !isSpreadsheet && !isPresentation && !isHtml && !isVideo && !isImage && (
                <FileIcon fontSize="large" color="action" />
              )}
            </Box>
            <Typography variant="body1">
              {attachmentUrl.split('/').pop()}
            </Typography>
          </Box>
        )}
      </Box>
      
      <CardContent sx={{ py: 1 }}>
        <Button 
          variant="outlined" 
          size="small" 
          fullWidth
          onClick={handleOpenAttachment}
        >
          Open Attachment
        </Button>
      </CardContent>
    </Card>
  );
};

// Multiple attachments viewer with navigation
const MultipleAttachmentsViewer = ({ attachments, currentIndex, onChangeIndex }) => {
  if (!attachments || attachments.length === 0) return null;
  
  const currentAttachment = attachments[currentIndex] || '';
  
  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* Navigation arrows - only show if there are multiple attachments */}
      {attachments.length > 1 && (
        <>
          <IconButton 
            sx={{ 
              position: 'absolute', 
              left: 10, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
            }}
            onClick={() => onChangeIndex(currentIndex > 0 ? currentIndex - 1 : attachments.length - 1)}
            aria-label="Previous attachment"
          >
            <ArrowBackIosIcon />
          </IconButton>
          
          <IconButton 
            sx={{ 
              position: 'absolute', 
              right: 10, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
            }}
            onClick={() => onChangeIndex((currentIndex + 1) % attachments.length)}
            aria-label="Next attachment"
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </>
      )}
      
      {/* Attachment counter */}
      {attachments.length > 1 && (
        <Box sx={{ 
          position: 'absolute', 
          top: 10, 
          right: 10, 
          zIndex: 2,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.875rem',
          fontWeight: 'medium'
        }}>
          {currentIndex + 1} / {attachments.length}
        </Box>
      )}
      
      {/* Attachment viewer */}
      <AttachmentViewer attachmentUrl={currentAttachment} />
    </Box>
  );
};

const TaskTracker = ({ initialTaskId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [newAttachmentUrl, setNewAttachmentUrl] = useState('');
  const [currentViewingAttachmentIndex, setCurrentViewingAttachmentIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [taskDialogTab, setTaskDialogTab] = useState(0);
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sortBy, setSortBy] = useState('creationDate');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [includeDeletedTasks, setIncludeDeletedTasks] = useState(false);
  const [includeCompletedTasks, setIncludeCompletedTasks] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [notificationsSupported, setNotificationsSupported] = useState(false);
  const [notificationsPermission, setNotificationsPermission] = useState('default');
  const [filters, setFilters] = useState({
    creationUserId: '',
    assignedUserId: localStorage.getItem('username') || '', // Default to current user if available
    priority: '',
    status: ''
  });
  
  // Task statuses and priorities for dropdown options
  const taskStatuses = ['Created', 'In Progress', 'Completed', 'Reopened'];
  const taskPriorities = ['Immediate', 'Within an hour', 'End of day', 'Specific Due Date'];

  // Helper function to create Eastern Time date strings
  const createEasternTimeDate = () => {
    // Create a new date in the local timezone
    const now = new Date();
    
    // Format the date in Eastern Time
    const options = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    
    // Get the formatted date parts in Eastern Time
    const etParts = new Intl.DateTimeFormat('en-US', options).formatToParts(now);
    
    // Map to get values from the formatted parts
    const etValues = {};
    etParts.forEach(part => {
      if (part.type !== 'literal') {
        etValues[part.type] = part.value;
      }
    });
    
    // Create ISO string in Eastern Time format YYYY-MM-DDTHH:MM:SS
    return `${etValues.year}-${etValues.month}-${etValues.day}T${etValues.hour}:${etValues.minute}:${etValues.second}`;
  };
  
  // Helper function to create SQLite-compatible date format (YYYY-MM-DD HH:MM:SS)
  const formatSQLiteDate = () => {
    // Create a new date in the local timezone
    const now = new Date();
    
    // Format the date in Eastern Time
    const options = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    
    // Get the formatted date parts in Eastern Time
    const etParts = new Intl.DateTimeFormat('en-US', options).formatToParts(now);
    
    // Map to get values from the formatted parts
    const etValues = {};
    etParts.forEach(part => {
      if (part.type !== 'literal') {
        etValues[part.type] = part.value;
      }
    });
    
    // Create SQLite-compatible date string in Eastern Time format YYYY-MM-DD HH:MM:SS
    return `${etValues.year}-${etValues.month}-${etValues.day} ${etValues.hour}:${etValues.minute}:${etValues.second}`;
  };

  // Effect to update due date when priority changes
  useEffect(() => {
    if (currentTask?.priority && currentTask.priority !== 'Specific Due Date') {
      // Set due date to current Eastern Time
      setCurrentTask(prev => ({
        ...prev,
        dateRequestedBy: createEasternTimeDate().slice(0, 16)
      }));
    }
  }, [currentTask?.priority]);

  // Fetch user list for Created By dropdown
  const fetchUserList = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token') || 'dummy-token';
      const response = await fetch('/api/auth/users-for-filter', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user list');
      }
      
      const data = await response.json();
      setUserList(data);
    } catch (err) {
      console.error('Error fetching user list:', err);
      setError('Failed to load user list. Please try again later.');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle task dialog tab change
  const handleTaskDialogTabChange = (event, newValue) => {
    setTaskDialogTab(newValue);
  };
  
  // Upload a blob URL to the server and return a server path
  const uploadBlobAttachment = async (blobUrl) => {
    try {
      if (!blobUrl || !blobUrl.startsWith('blob:')) {
        return blobUrl; // Return as-is if not a blob URL
      }
      
      console.log('Uploading blob attachment to server...');
      
      // Fetch the blob
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      
      // Create a file from the blob
      const filename = `task-attachment-${Date.now()}.${blob.type.split('/')[1] || 'png'}`;
      const file = new File([blob], filename, { type: blob.type });
      
      // Create and send FormData
      const formData = new FormData();
      formData.append('document', file);
      
      // Get token
      const token = localStorage.getItem('token') || 'dummy-token';
      
      // Upload to server
      const uploadResponse = await fetch('/upload-document', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Attachment upload failed');
      }
      
      const result = await uploadResponse.json();
      console.log('Blob attachment uploaded successfully:', result.file_path);
      
      // Don't revoke the blob URL until task is actually saved
      // This allows the image to be displayed in the UI
      // URL.revokeObjectURL(blobUrl);
      
      // Return the server path
      return result.file_path;
    } catch (error) {
      console.error('Error uploading blob attachment:', error);
      return blobUrl; // Return original URL as fallback
    }
  };
  
  // Handle attachment selection and upload if needed
  const handleSelectAttachment = async (url) => {
    if (!url) return;
    
    try {
      console.log('[DEBUG] handleSelectAttachment called with URL:', url);
      
      // If it's a blob URL, upload it to the server first
      if (url.startsWith('blob:')) {
        console.log('[DEBUG] Detected blob URL, uploading to server...');
        
        // Create a temporary placeholder in the attachments array
        const attachments = currentTask?.attachments || [];
        setCurrentTask(prev => ({
          ...prev,
          attachments: [...attachments, 'Uploading...']
        }));
        
        // Convert the blob URL to a File object
        const response = await fetch(url);
        const blob = await response.blob();
        const filename = `task-attachment-${Date.now()}.${blob.type.split('/')[1] || 'png'}`;
        const file = new File([blob], filename, { type: blob.type });
        
        // Create FormData and upload
        const formData = new FormData();
        formData.append('document', file);
        formData.append('name', filename);
        formData.append('description', 'Task attachment');
        
        // Get token
        const token = localStorage.getItem('token') || 'dummy-token';
        
        // Upload to server
        const uploadResponse = await fetch('/upload-document', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Attachment upload failed: ' + uploadResponse.statusText);
        }
        
        const result = await uploadResponse.json();
        console.log('[DEBUG] Upload successful, result:', result);
        
        // Extract the server path and store it
        // Make sure it starts with /uploads/ for correct server access
        let serverPath = result.file_path;
        if (serverPath && !serverPath.startsWith('/uploads/')) {
          // Extract filename from path and ensure proper path format
          const filename = serverPath.split('/').pop().split('\\').pop();
          serverPath = `/uploads/${filename}`;
        }
        
        console.log('[DEBUG] Using server path:', serverPath);
        
        // Update the attachments array, replacing the "Uploading..." placeholder
        const updatedAttachments = [...attachments];
        const uploadingIndex = updatedAttachments.indexOf('Uploading...');
        if (uploadingIndex >= 0) {
          updatedAttachments[uploadingIndex] = serverPath;
        } else {
          updatedAttachments.push(serverPath);
        }
        
        // Update task with the new attachments array
        setCurrentTask(prev => ({
          ...prev,
          attachments: updatedAttachments,
          // Also set single attachment for backward compatibility
          attachment: serverPath
        }));
        
        // Set the viewing index to the newly added attachment
        setCurrentViewingAttachmentIndex(updatedAttachments.length - 1);
      } else {
        // For non-blob URLs (like server paths), just add to attachments array
        console.log('[DEBUG] Using non-blob URL directly:', url);
        
        // Ensure proper format for server paths
        let processedUrl = url;
        if (!url.startsWith('http') && !url.startsWith('/uploads/') && !url.startsWith('blob:')) {
          // If it's just a filename, prepend /uploads/
          const filename = url.split('/').pop().split('\\').pop();
          processedUrl = `/uploads/${filename}`;
          console.log('[DEBUG] Reformatted URL to:', processedUrl);
        }
        
        // Add to attachments array
        const attachments = currentTask?.attachments || [];
        const updatedAttachments = [...attachments, processedUrl];
        
        setCurrentTask(prev => ({ 
          ...prev, 
          attachments: updatedAttachments,
          // Also set single attachment for backward compatibility
          attachment: processedUrl
        }));
        
        // Set the viewing index to the newly added attachment
        setCurrentViewingAttachmentIndex(updatedAttachments.length - 1);
      }
    } catch (error) {
      console.error('Error handling attachment:', error);
      alert('Failed to process attachment: ' + error.message);
      
      // Remove the "Uploading..." placeholder if there was an error
      const attachments = currentTask?.attachments || [];
      const updatedAttachments = attachments.filter(att => att !== 'Uploading...');
      
      setCurrentTask(prev => ({ 
        ...prev, 
        attachments: updatedAttachments 
      }));
    }
  };
  
  // Handle uploading multiple files at once
  const handleMultipleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Get current attachments
    const currentAttachments = currentTask?.attachments || [];
    
    // Show uploading placeholders for all files
    const placeholders = Array(files.length).fill('Uploading...');
    setCurrentTask(prev => ({
      ...prev,
      attachments: [...currentAttachments, ...placeholders]
    }));
    
    // Get auth token
    const token = localStorage.getItem('token') || 'dummy-token';
    
    // Track successful uploads to update the UI
    const successfulUploads = [];
    let lastUploadedPath = null;
    
    // Process each file
    for (let i = 0; i < files.length; i++) {
      try {
        const file = files[i];
        const formData = new FormData();
        formData.append('document', file);
        
        // Upload to server
        const uploadResponse = await fetch('/upload-document', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload file ${file.name}`);
        }
        
        const result = await uploadResponse.json();
        let serverPath = result.file_path;
        
        // Format server path consistently
        if (serverPath && !serverPath.startsWith('/uploads/')) {
          const filename = serverPath.split('/').pop().split('\\').pop();
          serverPath = `/uploads/${filename}`;
        }
        
        console.log(`Uploaded file ${i+1}/${files.length}: ${serverPath}`);
        
        // Add to successful uploads list
        successfulUploads.push(serverPath);
        lastUploadedPath = serverPath;
      } catch (error) {
        console.error(`Error uploading file ${i+1}/${files.length}:`, error);
      }
    }
    
    // Update the task with all successful uploads
    if (successfulUploads.length > 0) {
      // Remove placeholders and add successful uploads
      const finalAttachments = currentAttachments.filter(att => att !== 'Uploading...').concat(successfulUploads);
      
      setCurrentTask(prev => ({
        ...prev,
        attachments: finalAttachments,
        // Set the most recent upload as the single attachment for backward compatibility
        attachment: lastUploadedPath
      }));
      
      // Set the viewing index to the first newly added attachment
      setCurrentViewingAttachmentIndex(currentAttachments.length);
    } else {
      // If no uploads succeeded, just remove the placeholders
      setCurrentTask(prev => ({
        ...prev,
        attachments: prev.attachments.filter(att => att !== 'Uploading...')
      }));
    }
  };
  
  // Helper function to decode JWT token
  const decodeJWT = (token) => {
    try {
      if (token === 'dummy-token') {
        return { username: localStorage.getItem('username') || 'user' };
      }
      
      // JWT tokens are base64Url encoded in three parts: header.payload.signature
      const base64Url = token.split('.')[1]; // Get the payload part
      if (!base64Url) return null;
      
      // Convert base64Url to base64
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      // Decode the base64 string
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  // Fetch current user info
  const fetchCurrentUser = async () => {
    try {
      // First check localStorage for the username (highest priority)
      const storedUsername = localStorage.getItem('username');
      if (storedUsername) {
        setCurrentUser({ username: storedUsername });
        console.log('Current user set from localStorage:', { username: storedUsername });
        
        // Update the assignedUserId filter to show only the current user's tasks by default
        setFilters(prev => ({
          ...prev,
          assignedUserId: storedUsername
        }));
        
        return;
      }
      
      const token = localStorage.getItem('token') || 'dummy-token';
      
      // Try to get user from token
      const decodedToken = decodeJWT(token);
      let username = decodedToken?.username;
      
      // If we couldn't get username from token, try the API
      if (!username) {
        const response = await fetch('/api/auth/users/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
          console.log('Current user from API:', userData);
          
          // Update the assignedUserId filter with the username from API
          if (userData.username) {
            setFilters(prev => ({
              ...prev,
              assignedUserId: userData.username
            }));
          }
          
          return;
        } else {
          console.error('Failed to fetch user profile');
          // Default to a generic username instead of 'admin'
          username = 'user';
        }
      }
      
      // Use the username we found
      setCurrentUser({ username });
      console.log('Current user set to:', { username });
      
      // Update the assignedUserId filter with this username
      setFilters(prev => ({
        ...prev,
        assignedUserId: username
      }));
      
    } catch (err) {
      console.error('Error fetching user profile:', err);
      // Try to get username from localStorage as primary fallback
      const storedUsername = localStorage.getItem('username');
      if (storedUsername) {
        setCurrentUser({ username: storedUsername });
        
        // Update the assignedUserId filter with this username
        setFilters(prev => ({
          ...prev,
          assignedUserId: storedUsername
        }));
      } else {
        // Last resort fallbacks
        const token = localStorage.getItem('token') || 'dummy-token';
        const decodedToken = decodeJWT(token);
        const username = decodedToken?.username || 'user';
        setCurrentUser({ username });
        
        // Update the assignedUserId filter with this username
        setFilters(prev => ({
          ...prev,
          assignedUserId: username
        }));
      }
    }
  };

  // State for device detection
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  // Listen for notification click messages from service worker
  const listenForNotificationClicks = () => {
    console.log('Setting up notification click listener');
    if ('serviceWorker' in navigator) {
      // Add event listener for "message" events from the service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('Received message from service worker:', event.data);
        
        // Handle NOTIFICATION_CLICK type messages (original handler)
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          console.log('Received notification click data:', event.data);
          const clickData = event.data.payload;
          
          // If the click data includes a taskId, open that task
          if (clickData && clickData.taskId) {
            console.log('Opening task from notification click:', clickData.taskId);
            
            // Find the task with this ID
            const taskToOpen = tasks.find(task => 
              String(task.id) === String(clickData.taskId)
            );
            
            // If found, open the edit dialog for this task
            if (taskToOpen) {
              console.log('Found task, opening dialog:', taskToOpen);
              handleOpenEditDialog(taskToOpen);
            } else {
              console.log('Task not found, will need to fetch it');
              fetchTaskById(clickData.taskId);
            }
          }
        }
        
        // Handle NAVIGATE type messages (new handler for iOS)
        else if (event.data && event.data.type === 'NAVIGATE') {
          console.log('Received NAVIGATE message from service worker:', event.data);
          
          // Extract taskId from URL
          const url = event.data.url;
          if (url) {
            let taskId = null;
            
            // Handle different URL formats
            if (url.includes('?taskId=')) {
              // Handle URL format: /?taskId=123
              const match = url.match(/[?&]taskId=([^&]+)/);
              if (match && match[1]) {
                taskId = match[1];
              }
            } else if (url.includes('/task/')) {
              // Handle URL format: /task/123
              const match = url.match(/\/task\/([^/]+)/);
              if (match && match[1]) {
                taskId = match[1];
              }
            }
            
            if (taskId) {
              console.log('Extracted taskId from NAVIGATE message:', taskId);
              
              // Find the task with this ID
              const taskToOpen = tasks.find(task => 
                String(task.id) === String(taskId)
              );
              
              // If found, open the edit dialog for this task
              if (taskToOpen) {
                console.log('Found task, opening dialog:', taskToOpen);
                handleOpenEditDialog(taskToOpen);
              } else {
                console.log('Task not found, fetching from server');
                fetchTaskById(taskId);
              }
            }
          }
        }
      });
    }
  };

  // Check for notification click data stored by the service worker
  const checkForStoredNotificationClicks = () => {
    console.log('Checking for stored notification clicks...');
    try {
      // Track if we've found and processed notification data to avoid duplicates
      let notificationProcessed = false;
      
      // Check IndexedDB for click data with improved error handling
      const checkIndexedDB = async () => {
        if ('indexedDB' in window) {
          try {
            // Create a promise-wrapped version of IndexedDB access for better control
            const getIndexedDBData = () => {
              return new Promise((resolve, reject) => {
                try {
                  const dbOpenRequest = indexedDB.open('zg-notifications', 1);
                  
                  dbOpenRequest.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains('clicks')) {
                      db.createObjectStore('clicks', { keyPath: 'timestamp' });
                    }
                  };
                  
                  dbOpenRequest.onsuccess = (event) => {
                    try {
                      const db = event.target.result;
                      
                      // Handle case where the object store doesn't exist yet
                      if (!db.objectStoreNames.contains('clicks')) {
                        console.log('Clicks store not found in IndexedDB');
                        resolve(null);
                        return;
                      }
                      
                      const transaction = db.transaction(['clicks'], 'readwrite');
                      const store = transaction.objectStore('clicks');
                      const request = store.getAll();
                      
                      request.onsuccess = () => {
                        const clicksData = request.result;
                        // Clear the click data immediately to prevent reopening
                        store.clear().onsuccess = () => {
                          console.log('IndexedDB click store cleared');
                        };
                        
                        resolve(clicksData);
                      };
                      
                      request.onerror = (error) => {
                        console.error('Error getting IndexedDB clicks:', error);
                        reject(error);
                      };
                    } catch (innerError) {
                      console.error('Error in IndexedDB onsuccess handler:', innerError);
                      reject(innerError);
                    }
                  };
                  
                  dbOpenRequest.onerror = (error) => {
                    console.error('Error opening IndexedDB:', error);
                    reject(error);
                  };
                } catch (outerError) {
                  console.error('Error in IndexedDB setup:', outerError);
                  reject(outerError);
                }
              });
            };
            
            // Execute the promise-wrapped function
            const clicksData = await getIndexedDBData();
            
            if (clicksData && clicksData.length > 0 && !notificationProcessed) {
              console.log('Found notification click data in IndexedDB:', clicksData);
              // Sort by timestamp to get the most recent click
              clicksData.sort((a, b) => b.timestamp - a.timestamp);
              const mostRecentClick = clicksData[0];
              
              // Process the click data (open task, etc.)
              if (mostRecentClick.taskId) {
                console.log('Processing task ID from IndexedDB:', mostRecentClick.taskId);
                notificationProcessed = true;
                
                // Fetch the task and open it
                fetchTaskById(mostRecentClick.taskId);
                return true;
              }
            }
          } catch (error) {
            console.error('Error in IndexedDB processing:', error);
          }
        }
        return false;
      };
      
      // Check localStorage for click data
      const checkLocalStorage = () => {
        try {
          // Check for standard notification click data
          const clickDataString = localStorage.getItem('zg-notification-click');
          if (clickDataString && !notificationProcessed) {
            console.log('Found notification click data in localStorage:', clickDataString);
            try {
              const clickData = JSON.parse(clickDataString);
              
              // Clean up the storage to prevent reopening on refresh
              localStorage.removeItem('zg-notification-click');
              
              // Process the click data
              if (clickData && clickData.taskId) {
                console.log('Processing task ID from localStorage:', clickData.taskId);
                notificationProcessed = true;
                fetchTaskById(clickData.taskId);
                return true;
              }
            } catch (error) {
              console.error('Error parsing notification click data:', error);
            }
          }
          
          // iOS-specific: check for alternative storage keys
          // Service worker may use a different key format on iOS
          const keys = Object.keys(localStorage);
          const notificationKeys = keys.filter(key => 
            key.startsWith('zg-notification') || 
            key.includes('notification') || 
            key.includes('taskId')
          );
          
          for (const key of notificationKeys) {
            if (key === 'zg-notification-click') continue; // Already checked above
            
            try {
              const value = localStorage.getItem(key);
              console.log(`Checking alternative localStorage key: ${key} = ${value}`);
              
              if (value && !notificationProcessed) {
                let taskId = null;
                
                // Try to parse as JSON
                try {
                  const parsedData = JSON.parse(value);
                  taskId = parsedData.taskId || parsedData.id;
                } catch {
                  // If not JSON, check if it's a direct taskId
                  if (/^\d+$/.test(value)) {
                    taskId = value;
                  }
                }
                
                if (taskId) {
                  console.log(`Found taskId ${taskId} in localStorage key ${key}`);
                  localStorage.removeItem(key); // Clean up
                  notificationProcessed = true;
                  fetchTaskById(taskId);
                  return true;
                }
              }
            } catch (error) {
              console.error(`Error processing localStorage key ${key}:`, error);
            }
          }
        } catch (error) {
          console.error('Error checking localStorage:', error);
        }
        return false;
      };
      
      // Check URL parameters for notification=true
      const checkUrlParams = () => {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          // Check for explicit notification parameter
          if (urlParams.get('notification') === 'true' && urlParams.get('taskId') && !notificationProcessed) {
            const taskId = urlParams.get('taskId');
            console.log('Found notification taskId in URL params:', taskId);
            notificationProcessed = true;
            fetchTaskById(taskId);
            return true;
          }
          
          // Check for taskId parameter without notification=true (fallback)
          if (urlParams.get('taskId') && !notificationProcessed) {
            const taskId = urlParams.get('taskId');
            console.log('Found taskId in URL params (possible notification):', taskId);
            notificationProcessed = true;
            fetchTaskById(taskId);
            return true;
          }
        } catch (error) {
          console.error('Error checking URL parameters:', error);
        }
        return false;
      };
      
      // iOS-specific: add data to localStorage for iOS fallback method
      const setupiOSFallback = () => {
        if (isIOS()) {
          // Create a function that will be available to iOS Safari
          // when it opens the app from a notification
          window.handlePendingNotificationClick = (taskId) => {
            if (taskId) {
              console.log('handlePendingNotificationClick called with taskId:', taskId);
              localStorage.setItem('ios-pending-notification', taskId);
              fetchTaskById(taskId);
            }
          };
          
          // Check if there's a pending notification from a previous session
          const pendingTaskId = localStorage.getItem('ios-pending-notification');
          if (pendingTaskId && !notificationProcessed) {
            console.log('Found pending iOS notification taskId:', pendingTaskId);
            localStorage.removeItem('ios-pending-notification');
            notificationProcessed = true;
            fetchTaskById(pendingTaskId);
            return true;
          }
        }
        return false;
      };
      
      // Execute all checks in sequence
      // We run them in a specific order of reliability
      const runAllChecks = async () => {
        // Most reliable first: URL parameters
        if (checkUrlParams()) return;
        
        // Second most reliable: IndexedDB
        if (await checkIndexedDB()) return;
        
        // Third: localStorage
        if (checkLocalStorage()) return;
        
        // iOS-specific fallback
        if (setupiOSFallback()) return;
        
        console.log('No notification click data found.');
      };
      
      // Start the checks
      runAllChecks();
      
    } catch (error) {
      console.error('Error checking for stored notification clicks:', error);
    }
  };
  
  // Fetch a specific task by ID and open it
  const fetchTaskById = async (taskId) => {
    if (!taskId) return;
    
    console.log('Fetching task by ID:', taskId);
    try {
      const token = localStorage.getItem('token') || 'dummy-token';
      const response = await fetch(`/api/tasks/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch task');
      }
      
      const task = await response.json();
      if (task) {
        console.log('Successfully fetched task:', task);
        handleOpenEditDialog(task);
      }
    } catch (error) {
      console.error('Error fetching task by ID:', error);
    }
  };

  // Initialize notifications
  useEffect(() => {
    const setupNotifications = async () => {
      try {
        // Check if this is an iOS device
        const iosDevice = isIOS();
        setIsIOSDevice(iosDevice);
        
        // Check if push notifications are supported
        const supported = await isPushSupported();
        setNotificationsSupported(supported);
        
        if (supported) {
          // Initialize notifications
          await initializeNotifications();
          
          // Check current permission status
          const permission = await Notification.permission;
          setNotificationsPermission(permission);
          
          // Request permission if not already granted
          if (permission !== 'granted') {
            const newPermission = await requestNotificationPermission();
            setNotificationsPermission(newPermission);
          }
        } else {
          console.log('Push notifications are not supported in this browser');
        }
      } catch (error) {
        console.error('Error setting up notifications:', error);
      }
    };
    
  // First fetch the current user to get the proper filter
  fetchCurrentUser();
  
  // Then fetch tasks with the updated filter
  fetchTasks();
  fetchUserList();
  setupNotifications();
  
  // Set up notification click listener
  listenForNotificationClicks();
  
  // Check for stored notification clicks
  checkForStoredNotificationClicks();
  
  // Enhanced visibility change detection for iOS
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      console.log('Document became visible, checking for notification clicks');
      checkForStoredNotificationClicks();
    }
  };
  
  // Add event listener for visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // iOS-specific: also check on focus events which can be more reliable on iOS
  const handleWindowFocus = () => {
    console.log('Window focused, checking for notification clicks');
    setTimeout(checkForStoredNotificationClicks, 300); // Short delay for reliability
  };
  window.addEventListener('focus', handleWindowFocus);
  
  // iOS Safari sometimes needs a pageshow event
  const handlePageShow = (event) => {
    // The persisted property is true if the page is loaded from the back/forward cache
    if (event.persisted) {
      console.log('Page restored from back/forward cache, checking for notification clicks');
      setTimeout(checkForStoredNotificationClicks, 500);
    }
  };
  window.addEventListener('pageshow', handlePageShow);
  
  // Clean up event listeners on unmount
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleWindowFocus);
    window.removeEventListener('pageshow', handlePageShow);
  };
  }, []);
  
  // Local state to track the effective task ID (cleared when dialog is closed)
  const [effectiveTaskId, setEffectiveTaskId] = useState(initialTaskId);

  // Reset effective task ID when initialTaskId changes
  useEffect(() => {
    setEffectiveTaskId(initialTaskId);
  }, [initialTaskId]);

  // Handle initialTaskId - open task automatically when specified
  useEffect(() => {
    // Only try to open a task if effectiveTaskId is provided and tasks are loaded
    if (effectiveTaskId && !loading && tasks.length > 0) {
      console.log(`Looking for task with ID: ${effectiveTaskId}`);
      // Find the task with the matching ID
      const taskToOpen = tasks.find(task => String(task.id) === String(effectiveTaskId));
      
      // If found, open the edit dialog for this task
      if (taskToOpen) {
        console.log(`Found task, opening dialog:`, taskToOpen);
        handleOpenEditDialog(taskToOpen);
      } else {
        console.log(`Task with ID ${effectiveTaskId} not found in loaded tasks`);
      }
    }
  }, [effectiveTaskId, tasks, loading]); // Dependencies: re-run when effectiveTaskId changes

  // Fetch staff and user lists when dialog opens
  useEffect(() => {
    if (openTaskDialog) {
      fetchUserList();
    }
  }, [openTaskDialog]);
  
  // Fetch staff and user lists when filters section is shown
  useEffect(() => {
    if (showFilters) {
      fetchUserList();
    }
  }, [showFilters]);
  
  // Refresh task list when filters change
  useEffect(() => {
    // Only fetch tasks if we've already loaded the initial data
    if (currentUser) {
      fetchTasks();
    }
  }, [filters.assignedUserId]); // Re-fetch when this specific filter changes
  const fetchTasks = async () => {
    // Clear any taskId from URL when refreshing to prevent reopening the same task
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('taskId')) {
      // Use replaceState to modify the URL without reloading the page
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('taskId');
      window.history.replaceState({}, '', newUrl.toString());
      console.log('Cleared taskId from URL when refreshing tasks');
      
      // Clear the effective task ID to prevent reopening on refresh
      setEffectiveTaskId(null);
    }
    
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || 'dummy-token';
      // Convert filters to query string parameters
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });
      
      const response = await fetch(`/api/tasks?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }
      
      const data = await response.json();
      setTasks(data);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    // Prioritize username from localStorage first
    let username = localStorage.getItem('username');
    
    // If not in localStorage, try from currentUser or other sources
    if (!username) {
      username = currentUser?.username;
    }
    
    // Last resort fallbacks
    if (!username) {
      const token = localStorage.getItem('token') || 'dummy-token';
      const decodedToken = decodeJWT(token);
      username = decodedToken?.username || 'user';
    }
    
    setCurrentTask({
      description: '',
      priority: 'Medium',
      status: 'Created',
      assignedUserId: '',
      dateRequestedBy: '',
      note: '',
      attachments: [], // Initialize with empty array for multiple attachments
      creationUserId: username
    });
    
    // Always store the username we're using in localStorage for future use
    if (username) {
      localStorage.setItem('username', username);
    }
    
    setOpenTaskDialog(true);
  };

  const handleOpenEditDialog = async (task) => {
    // Ensure the task has an attachments array
    let updatedTask = { ...task };
    let restoredAttachments = [];
    let restorationSuccessful = false;
    
    // APPROACH 1: First try to extract attachments from the note field (most reliable)
    if (task.note && typeof task.note === 'string') {
      try {
        // Look for our special attachment markers
        const attachmentMarker = '<!-- ZGATTACHMENTS:';
        const attachmentEndMarker = ':ZGATTACHMENTSEND -->';
        
        if (task.note.includes(attachmentMarker) && task.note.includes(attachmentEndMarker)) {
          // Extract the JSON string between markers
          const startIndex = task.note.indexOf(attachmentMarker) + attachmentMarker.length;
          const endIndex = task.note.indexOf(attachmentEndMarker);
          const attachmentsJson = task.note.substring(startIndex, endIndex);
          
          // Parse the JSON to get the attachments array
          restoredAttachments = JSON.parse(attachmentsJson);
          console.log('Successfully restored attachments from note field:', restoredAttachments);
          
          // Clean the note field for display by removing the attachment data
          const cleanNote = task.note.substring(0, task.note.indexOf(attachmentMarker)).trim();
          updatedTask.note = cleanNote;
          
          restorationSuccessful = true;
        }
      } catch (error) {
        console.error('Error extracting attachments from note field:', error);
      }
    }
    
    // APPROACH 2: If that failed, try the serializedAttachments field
    if (!restorationSuccessful && task.serializedAttachments) {
      try {
        console.log('Found serializedAttachments field, restoring:', task.serializedAttachments);
        restoredAttachments = JSON.parse(task.serializedAttachments);
        console.log('Successfully restored attachments from serializedAttachments:', restoredAttachments);
        restorationSuccessful = true;
      } catch (error) {
        console.error('Error restoring serialized attachments:', error);
      }
    }
    
    // APPROACH 3: Fall back to single attachment if nothing else worked
    if (!restorationSuccessful && task.attachment) {
      console.log('Using single attachment as fallback:', task.attachment);
      restoredAttachments = [task.attachment];
      restorationSuccessful = true;
    }
    
    // Update the task with restored attachments
    updatedTask.attachments = restoredAttachments;
    
    // If any attachment is a blob URL, upload it first
    const attachmentsToProcess = updatedTask.attachments.filter(att => att && att.startsWith('blob:'));
    if (attachmentsToProcess.length > 0) {
      // Create a copy of attachments for processing
      const processedAttachments = [...updatedTask.attachments];
      
      // Process each blob attachment
      for (let i = 0; i < processedAttachments.length; i++) {
        const attachment = processedAttachments[i];
        if (attachment && attachment.startsWith('blob:')) {
          try {
            const serverPath = await uploadBlobAttachment(attachment);
            processedAttachments[i] = serverPath;
          } catch (error) {
            console.error('Failed to upload blob attachment:', error);
            // Keep the blob URL if upload fails
          }
        }
      }
      
      // Update the task with processed attachments
      updatedTask.attachments = processedAttachments;
      
      // Also update the single attachment field for backwards compatibility
      if (processedAttachments.length > 0) {
        updatedTask.attachment = processedAttachments[0];
      }
    }
    
    // Set the current task and open dialog
    setCurrentTask(updatedTask);
    setCurrentViewingAttachmentIndex(0); // Reset to first attachment
    setOpenTaskDialog(true);
  };

  const handleCloseDialog = () => {
    // Clear taskId from URL when closing a task dialog
    // This prevents the same task from being reopened when new notifications arrive
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('taskId')) {
      // Use replaceState to modify the URL without reloading the page
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('taskId');
      window.history.replaceState({}, '', newUrl.toString());
      console.log('Cleared taskId from URL');
    }
    
    // Clear the effective task ID to prevent reopening on refresh
    setEffectiveTaskId(null);
    console.log('Cleared effective task ID to prevent reopening on refresh');
    
    setOpenTaskDialog(false);
    setCurrentTask(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for priority changes
    if (name === 'priority') {
      if (value !== 'Specific Due Date') {
        // Set due date to current Eastern Time
        setCurrentTask(prev => ({ 
          ...prev, 
          [name]: value,
          dateRequestedBy: createEasternTimeDate().slice(0, 16)
        }));
      } else {
        // For "Specific Due Date", just update the priority
        setCurrentTask(prev => ({ ...prev, [name]: value }));
      }
    } else {
      // Normal handling for other fields
      setCurrentTask(prev => ({ ...prev, [name]: value }));
    }
  };
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveTask = async () => {
    try {
      const token = localStorage.getItem('token') || 'dummy-token';
      const method = currentTask.id ? 'PUT' : 'POST';
      const url = currentTask.id ? `/api/tasks/${currentTask.id}` : '/api/tasks';
      
      // Create a copy of the task for saving
      let taskToSave = {...currentTask};
      
      // Track if assignment has changed - for notifications
      let isNewAssignment = false;
      let previousAssignedUser = null;
      
      if (currentTask.id) {
        // For existing tasks, check if the assigned user has changed
        const existingTask = tasks.find(t => t.id === currentTask.id);
        if (existingTask) {
          previousAssignedUser = existingTask.assignedUserId;
          if (previousAssignedUser !== currentTask.assignedUserId) {
            isNewAssignment = true;
          }
        }
      } else {
        // For new tasks, if assignedUserId is set, it's a new assignment
        isNewAssignment = !!currentTask.assignedUserId;
      }
      
      // For new tasks (no ID), ensure creationUserId is set
      if (!taskToSave.id) {
        // Prioritize username from localStorage first
        let username = localStorage.getItem('username');
        
        // If not in localStorage, try from currentUser or other sources
        if (!username) {
          username = currentUser?.username;
        }
        
        // Last resort fallbacks
        if (!username) {
          const decodedToken = decodeJWT(token);
          username = decodedToken?.username || 'user';
        }
        
        taskToSave.creationUserId = username;
        
        // Always store the username we're using for future reference
        if (username) {
          localStorage.setItem('username', username);
        }
      }
      
      // Process attachments - ensuring no blob URLs remain and multiple attachments are preserved
      if (taskToSave.attachments && taskToSave.attachments.length > 0) {
        const processedAttachments = [...taskToSave.attachments];
        
        for (let i = 0; i < processedAttachments.length; i++) {
          const attachment = processedAttachments[i];
          if (attachment && attachment.startsWith('blob:')) {
            const serverPath = await uploadBlobAttachment(attachment);
            processedAttachments[i] = serverPath;
          }
        }
        
        // Update attachments array with processed paths
        taskToSave.attachments = processedAttachments;
        
        // Store all attachments in a serialized format in a custom field
        // This ensures they're preserved even if the server API doesn't support the attachments array
        taskToSave.serializedAttachments = JSON.stringify(processedAttachments);
        
        // ENHANCEMENT: Also store attachments in the note field to ensure they persist
        // This uses HTML comments so it won't be visible to users in the UI
        const attachmentsJson = JSON.stringify(processedAttachments);
        // Create a marker that can be detected when reopening the task
        const attachmentMarker = `<!-- ZGATTACHMENTS:${attachmentsJson}:ZGATTACHMENTSEND -->`;
        
        // Preserve any existing note content by adding the marker at the end
        if (!taskToSave.note) {
          taskToSave.note = attachmentMarker;
        } else {
          // Remove any existing attachment marker before adding a new one
          let cleanNote = taskToSave.note;
          if (cleanNote.includes('<!-- ZGATTACHMENTS:')) {
            cleanNote = cleanNote.split('<!-- ZGATTACHMENTS:')[0].trim();
          }
          taskToSave.note = `${cleanNote}\n${attachmentMarker}`;
        }
        
        // Also update the single attachment field for backwards compatibility
        if (processedAttachments.length > 0) {
          taskToSave.attachment = processedAttachments[0];
        }
      }
      
      // For backward compatibility, also handle single attachment field
      if (taskToSave.attachment && taskToSave.attachment.startsWith('blob:')) {
        if (!taskToSave.serverAttachmentPath) {
          // Upload if not already uploaded
          console.log('Uploading attachment before saving...');
          taskToSave.serverAttachmentPath = await uploadBlobAttachment(taskToSave.attachment);
        }
        // Use the server path for saving
        taskToSave.attachment = taskToSave.serverAttachmentPath;
        delete taskToSave.serverAttachmentPath;
        
        // Update attachments array if it exists
        if (taskToSave.attachments) {
          // Find and replace the blob URL if it exists in the array
          const blobIndex = taskToSave.attachments.findIndex(att => att === taskToSave.attachment);
          if (blobIndex >= 0) {
            taskToSave.attachments[blobIndex] = taskToSave.attachment;
          }
        }
      }
      
    // For new tasks, explicitly set the creation date in Eastern Time
    if (!taskToSave.id) {
      // New task - explicitly set creation date in Eastern Time
      taskToSave.creationDate = createEasternTimeDate();
    }
    
    // Always ensure dateCompleted is set for any Completed tasks
    if (taskToSave.status === 'Completed') {
      console.log('[DEBUG] Ensuring dateCompleted is set for completed task');
      // ALWAYS set dateCompleted for completed tasks, using SQLite-compatible format
      // Force the value to be set regardless of existing value
      const sqliteDate = formatSQLiteDate();
      taskToSave.dateCompleted = sqliteDate;
      console.log('[DEBUG] Task completion date explicitly set to SQLite format:', sqliteDate);
    }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(taskToSave)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save task');
      }
      
      // Get the saved task data and update currentTask with it
      const savedTask = await response.json();
      if (savedTask) {
        console.log('[DEBUG] Task saved successfully, server returned:', savedTask);
        // If the server returned the saved task, update currentTask completely
        setCurrentTask(savedTask);
        
            // Remove client-side notification sending - the server already handles this
            // This prevents duplicate notifications for task assignments
            console.log('Task saved successfully - server will handle any required notifications');
      }
      
      // Refresh task list after successful save
      fetchTasks();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving task:', err);
      setError('Failed to save task. Please try again.');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const token = localStorage.getItem('token') || 'dummy-token';
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete task');
      }
      
      // Refresh task list after successful deletion
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    }
  };
  
  // Function to directly reopen a task
  const handleReopenTask = async () => {
    try {
      const token = localStorage.getItem('token') || 'dummy-token';
      
      // Get the current username using the same approach as elsewhere in the component
      // First try localStorage, then currentUser, then fallback options
      let username = localStorage.getItem('username');
      if (!username) {
        username = currentUser?.username;
      }
      if (!username) {
        const decodedToken = decodeJWT(token);
        username = decodedToken?.username || 'user';
      }
      
      // Create the updated task object with required changes
      const updatedTask = {
        ...currentTask,
        status: 'Reopened',
        reopenedUserId: username, // Using actual username now instead of token
        reopenedDate: createEasternTimeDate(),
      // Clear the completion data as requested
      completionUserId: null,
      dateCompleted: null
    };
      
      // Send the API request to update the task
      const response = await fetch(`/api/tasks/${currentTask.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedTask)
      });
      
      if (!response.ok) {
        throw new Error('Failed to reopen task');
      }
      
      // Close the dialog and refresh the task list
      handleCloseDialog();
      fetchTasks();
    } catch (err) {
      console.error('Error reopening task:', err);
      setError('Failed to reopen task. Please try again.');
    }
  };

  // Helper function to normalize username for consistent comparison
  const normalizeUsername = (username) => {
    if (!username) return '';
    // Convert to string, trim whitespace, and lowercase for consistent comparison
    return String(username).trim().toLowerCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Enhanced debug logging to trace date conversion issues
      console.log('Formatting date string:', dateString, 'Type:', typeof dateString);
      
      // Special handling for SQLite datetime format (YYYY-MM-DD HH:MM:SS) from database
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        console.log('Detected SQLite date format:', dateString);
        
        // Parse the date parts manually for more precise control
        const parts = dateString.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
        if (parts) {
          const [_, year, month, day, hour, minute, second] = parts;
          
          // Create a date object with the parsed parts (assuming Eastern Time)
          // Note: Months in JS Date are 0-indexed, so subtract 1 from month
          const date = new Date(year, month - 1, day, hour, minute, second);
          
          // Format with Intl.DateTimeFormat in Eastern Time with the desired format
          const options = {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          };
          
          const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
          console.log('Successfully formatted SQLite date:', dateString, '→', formattedDate);
          return formattedDate;
        }
      }
      
      // Handle ISO format dates (with 'T')
      if (typeof dateString === 'string' && dateString.includes('T')) {
        console.log('Detected ISO date format:', dateString);
        
        // Create a date object directly from ISO string
        const date = new Date(dateString);
        
        // Verify the date is valid
        if (isNaN(date.getTime())) {
          console.error('Invalid ISO date:', dateString);
          return dateString || '-';
        }
        
        // Format with Intl.DateTimeFormat in Eastern Time
        const options = {
          timeZone: 'America/New_York',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };
        
        const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
        console.log('Successfully formatted ISO date:', dateString, '→', formattedDate);
        return formattedDate;
      }
      
      // For any other date format, try standard Date parsing
      console.log('Using standard date parsing for:', dateString);
      const date = new Date(dateString);
      
      // Verify the date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date format:', dateString);
        return dateString || '-';
      }
      
      // Format with Intl.DateTimeFormat in Eastern Time
      const options = {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
      console.log('Successfully formatted standard date:', dateString, '→', formattedDate);
      return formattedDate;
    } catch (error) {
      console.error('Error formatting date:', error, 'for input:', dateString);
      return dateString || '-';
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'Created': return 'primary';
      case 'In Progress': return 'secondary';
      case 'Completed': return 'success';
      case 'Reopened': return 'warning';
      default: return 'default';
    }
  };

  // Helper function to check if a task is assigned to the current user
  const isAssignedToCurrentUser = (assignedUserId) => {
    if (!assignedUserId) return false;
    
    // Get current user from different potential sources
    const storedUsername = localStorage.getItem('username');
    const currentUserName = currentUser?.username;
    
    // Normalize all usernames for comparison
    const normalizedAssignedUser = normalizeUsername(assignedUserId);
    const normalizedStoredUser = normalizeUsername(storedUsername);
    const normalizedCurrentUser = normalizeUsername(currentUserName);
    
    // Check if the assigned user matches any of the current user identifiers
    return normalizedAssignedUser === normalizedStoredUser || 
           normalizedAssignedUser === normalizedCurrentUser;
  };

  const getPriorityChipColor = (priority) => {
    switch (priority) {
      case 'Low': return 'success';
      case 'Medium': return 'info';
      case 'High': return 'warning';
      case 'Critical': return 'error';
      default: return 'default';
    }
  };

  // Handle sorting when a column header is clicked
  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it as sort column and default to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  // Function to sort tasks based on current sort column and direction
  const getSortedTasks = () => {
    if (!tasks || tasks.length === 0) return [];
    
    // Filter tasks based on includeDeletedTasks and includeCompletedTasks settings
    const filteredTasks = tasks.filter(task => {
      // Filter out deleted tasks if includeDeletedTasks is false
      if (!includeDeletedTasks && task.deleteDate) {
        return false;
      }
      
      // Filter out completed tasks if includeCompletedTasks is false
      if (!includeCompletedTasks && task.dateCompleted) {
        return false;
      }
      
      return true;
    });
    
    return [...filteredTasks].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Handle date columns
      if (sortBy === 'creationDate' || sortBy === 'dateRequestedBy' || sortBy === 'dateCompleted') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } 
      // Handle string columns - convert to lowercase for case-insensitive sorting
      else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Calculate sort value based on direction
      const compareValue = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  };
  return (
      <Box sx={{ p: '16px 16px 0 16px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, alignItems: 'center', pt: 1 }}>
        <Typography variant="h6" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
          Task Tracker
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Button 
            startIcon={<FilterIcon />} 
            variant={showFilters ? "contained" : "outlined"} 
            sx={{ mx: 1, fontSize: '0.85rem', py: 0.5 }}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button 
            startIcon={<RefreshIcon />} 
            variant="outlined" 
            sx={{ mx: 1, fontSize: '0.85rem', py: 0.5 }}
            onClick={fetchTasks}
          >
            Refresh
          </Button>
          <Button 
            startIcon={<AddIcon />} 
            variant="contained" 
            sx={{ mx: 1, fontSize: '0.85rem', py: 0.5 }}
            onClick={handleOpenCreateDialog}
          >
            New Task
          </Button>
        </Box>
      </Box>
      
      
      {/* Task Visibility Controls */}
      <Paper sx={{ p: 1, mb: 2, bgcolor: '#f8f9fa', mt: 1 }}>
        <Grid container spacing={1} direction={isMobile ? "column" : "row"} alignItems="flex-start">
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox 
                checked={includeDeletedTasks}
                onChange={(e) => setIncludeDeletedTasks(e.target.checked)}
                id="include-deleted-tasks"
              />
              <Typography 
                component="label" 
                htmlFor="include-deleted-tasks" 
                variant="body2"
                sx={{ cursor: 'pointer' }}
              >
                Include deleted tasks
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox 
                checked={includeCompletedTasks}
                onChange={(e) => setIncludeCompletedTasks(e.target.checked)}
                id="include-completed-tasks"
              />
              <Typography 
                component="label" 
                htmlFor="include-completed-tasks" 
                variant="body2"
                sx={{ cursor: 'pointer' }}
              >
                Include completed tasks
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {/* Filters Section */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Created By</InputLabel>
                <Select
                  name="creationUserId"
                  value={filters.creationUserId}
                  label="Created By"
                  onChange={handleFilterChange}
                  disabled={loadingUsers}
                  startAdornment={
                    loadingUsers ? (
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    ) : null
                  }
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {userList.map((user) => (
                    <MenuItem 
                      key={user.username} 
                      value={user.username}
                    >
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Assigned To</InputLabel>
                <Select
                  name="assignedUserId"
                  value={filters.assignedUserId}
                  label="Assigned To"
                  onChange={handleFilterChange}
                  disabled={loadingUsers}
                  startAdornment={
                    loadingUsers ? (
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    ) : null
                  }
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {userList.map((user) => (
                    <MenuItem 
                      key={user.username} 
                      value={user.username}
                    >
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={filters.priority}
                  label="Priority"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  {taskPriorities.map(priority => (
                    <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={filters.status}
                  label="Status"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  {taskStatuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button 
                variant="contained" 
                onClick={fetchTasks}
                sx={{ mr: 1 }}
              >
                Apply Filters
              </Button>
              <Button 
                variant="outlined"
                onClick={() => {
                  setFilters({
                    creationUserId: '',
                    assignedUserId: '',
                    priority: '',
                    status: ''
                  });
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
      {/* Error Message */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {/* Tasks Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tasks table">
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('description')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'description' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Description {sortBy === 'description' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('priority')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'priority' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Priority {sortBy === 'priority' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('status')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'status' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Status {sortBy === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('assignedUserId')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'assignedUserId' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Assigned To {sortBy === 'assignedUserId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('creationUserId')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'creationUserId' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Created By {sortBy === 'creationUserId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('creationDate')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'creationDate' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Created {sortBy === 'creationDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('dateRequestedBy')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'dateRequestedBy' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Due By {sortBy === 'dateRequestedBy' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('completionUserId')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'completionUserId' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Completed By {sortBy === 'completionUserId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('dateCompleted')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  fontWeight: sortBy === 'dateCompleted' ? 'bold' : 'inherit',
                  color: 'white'
                }}
              >
                Completion Date {sortBy === 'dateCompleted' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} sx={{ my: 3 }} />
                </TableCell>
              </TableRow>
            ) : tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" sx={{ py: 3 }}>
                    No tasks found. Create a new task to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              getSortedTasks().map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell 
                    sx={{ 
                      maxWidth: 300, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    onClick={() => handleOpenEditDialog(task)}
                  >
                    <Tooltip title={task.description}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'primary.main', 
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {task.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={task.priority} 
                      size="small" 
                      color={getPriorityChipColor(task.priority)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={task.status} 
                      size="small" 
                      color={getStatusChipColor(task.status)}
                    />
                  </TableCell>
                  <TableCell>{task.assignedUserId || '-'}</TableCell>
                  <TableCell>{task.creationUserId || '-'}</TableCell>
                  <TableCell>{task.creationDate ? formatDate(task.creationDate) : '-'}</TableCell>
                  <TableCell>{task.dateRequestedBy ? formatDate(task.dateRequestedBy) : '-'}</TableCell>
                  <TableCell>{task.completionUserId || '-'}</TableCell>
                  <TableCell>{task.dateCompleted ? formatDate(task.dateCompleted) : '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Task Dialog */}
      <Dialog 
        open={openTaskDialog} 
        onClose={handleCloseDialog} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : 'auto',
            maxHeight: isMobile ? '100%' : '90vh',
            m: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 1,
            width: isMobile ? '100%' : undefined,
            minWidth: { sm: '1000px' },
            // Adjust minHeight to leave room for buttons
            minHeight: { sm: '650px' }
          }
        }}
      >
        <DialogTitle>
          {currentTask?.id ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
      <DialogContent sx={{ pt: 2, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {isMobile ? (
            // Mobile Layout - Form fields on top, attachment viewer below
            <Box>
              {/* Form fields section */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={currentTask?.description || ''}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      name="priority"
                      value={currentTask?.priority || 'Medium'}
                      label="Priority"
                      onChange={handleChange}
                    >
                      {taskPriorities.map(priority => (
                        <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={currentTask?.status || 'Created'}
                      label="Status"
                      onChange={handleChange}
                    >
                      {taskStatuses.map(status => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                      name="assignedUserId"
                      value={currentTask?.assignedUserId || ''}
                      label="Assigned To"
                      onChange={handleChange}
                      disabled={loadingUsers}
                      startAdornment={
                        loadingUsers ? (
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        ) : null
                      }
                    >
                      <MenuItem value="">
                        <em>Not Assigned</em>
                      </MenuItem>
                      {userList.map((user) => (
                        <MenuItem 
                          key={user.username} 
                          value={user.username}
                        >
                          {user.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Due By"
                    name="dateRequestedBy"
                    type="datetime-local"
                    value={currentTask?.dateRequestedBy 
                      ? currentTask.dateRequestedBy.includes('T') 
                        ? currentTask.dateRequestedBy.slice(0, 16) 
                        : currentTask.dateRequestedBy + 'T00:00' 
                      : ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={currentTask?.priority !== 'Specific Due Date'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="note"
                    value={currentTask?.note || ''}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Attachments
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="Attachment URL"
                        name="newAttachmentUrl"
                        value={newAttachmentUrl}
                        onChange={(e) => setNewAttachmentUrl(e.target.value)}
                        placeholder="Enter URL or use upload button"
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (newAttachmentUrl) {
                            // Add new attachment to the array
                            const attachments = currentTask?.attachments || [];
                            setCurrentTask(prev => ({
                              ...prev,
                              attachments: [...attachments, newAttachmentUrl],
                              attachment: newAttachmentUrl // For backward compatibility
                            }));
                            setNewAttachmentUrl(''); // Clear input
                            setCurrentViewingAttachmentIndex(attachments.length); // View the new attachment
                          }
                        }}
                        disabled={!newAttachmentUrl}
                      >
                        Add
                      </Button>
                      <input
                        accept="image/*"
                        id="upload-screenshots-mobile"
                        type="file"
                        multiple  // Enable multiple file selection
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleMultipleFileUpload(e.target.files);
                          }
                        }}
                      />
                      <label htmlFor="upload-screenshots-mobile">
                        <Tooltip title="Select multiple files (hold Ctrl/Cmd)">
                          <IconButton component="span" color="primary">
                            <PhotoCameraIcon />
                          </IconButton>
                        </Tooltip>
                      </label>
                    </Box>
                    
                    {/* Display current attachments */}
                    {currentTask?.attachments && currentTask.attachments.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Current attachments ({currentTask.attachments.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          {currentTask.attachments.map((attachment, index) => (
                            <Chip 
                              key={index}
                              label={attachment.split('/').pop().split('\\').pop()}
                              onClick={() => setCurrentViewingAttachmentIndex(index)}
                              onDelete={() => {
                                // Remove this attachment
                                const updatedAttachments = currentTask.attachments.filter((_, i) => i !== index);
                                setCurrentTask(prev => ({
                                  ...prev,
                                  attachments: updatedAttachments,
                                  // Update single attachment field if removing the current one
                                  attachment: prev.attachment === attachment && updatedAttachments.length > 0
                                    ? updatedAttachments[0]
                                    : prev.attachment === attachment
                                      ? ''
                                      : prev.attachment
                                }));
                                // Reset viewing index if needed
                                if (currentViewingAttachmentIndex >= updatedAttachments.length) {
                                  setCurrentViewingAttachmentIndex(Math.max(0, updatedAttachments.length - 1));
                                }
                              }}
                              sx={{ 
                                maxWidth: '150px',
                                bgcolor: currentViewingAttachmentIndex === index ? 'primary.main' : 'default',
                                color: currentViewingAttachmentIndex === index ? 'white' : 'inherit'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
              
              {/* Attachment viewer section - below form fields */}
              {currentTask?.attachments && currentTask.attachments.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: '350px',
                  border: '3px solid #2196f3',
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <Box sx={{
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#2196f3',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      }}
                    >
                      Attachment Viewer
                    </Typography>
                    
                    {/* Attachment counter for mobile */}
                    {currentTask.attachments.length > 1 && (
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {currentViewingAttachmentIndex + 1} / {currentTask.attachments.length}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ 
                    flex: 1,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    position: 'relative',
                    minHeight: '300px'
                  }}>
                    <MultipleAttachmentsViewer 
                      attachments={currentTask.attachments}
                      currentIndex={currentViewingAttachmentIndex}
                      onChangeIndex={setCurrentViewingAttachmentIndex}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            // Desktop Layout - Original side-by-side layout
            <Grid container spacing={3}>
              {/* Left column - Form fields */}
              <Grid item xs={12} md={currentTask?.attachments?.length > 0 ? 5 : 12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={currentTask?.description || ''}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        name="priority"
                        value={currentTask?.priority || 'Medium'}
                        label="Priority"
                        onChange={handleChange}
                      >
                        {taskPriorities.map(priority => (
                          <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={currentTask?.status || 'Created'}
                        label="Status"
                        onChange={handleChange}
                      >
                        {taskStatuses.map(status => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Assigned To</InputLabel>
                      <Select
                        name="assignedUserId"
                        value={currentTask?.assignedUserId || ''}
                        label="Assigned To"
                        onChange={handleChange}
                        disabled={loadingUsers}
                        startAdornment={
                          loadingUsers ? (
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                          ) : null
                        }
                      >
                        <MenuItem value="">
                          <em>Not Assigned</em>
                        </MenuItem>
                        {userList.map((user) => (
                          <MenuItem 
                            key={user.username} 
                            value={user.username}
                          >
                            {user.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Due By"
                      name="dateRequestedBy"
                      type="datetime-local"
                      value={currentTask?.dateRequestedBy 
                        ? currentTask.dateRequestedBy.includes('T') 
                          ? currentTask.dateRequestedBy.slice(0, 16) 
                          : currentTask.dateRequestedBy + 'T00:00' 
                        : ''}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      disabled={currentTask?.priority !== 'Specific Due Date'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      name="note"
                      value={currentTask?.note || ''}
                      onChange={handleChange}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Attachments
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          fullWidth
                          label="Add Attachment URL"
                          value={newAttachmentUrl}
                          onChange={(e) => setNewAttachmentUrl(e.target.value)}
                          placeholder="Enter attachment URL or use upload button"
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (newAttachmentUrl) {
                              // Add new attachment to the array
                              const attachments = currentTask?.attachments || [];
                              setCurrentTask(prev => ({
                                ...prev,
                                attachments: [...attachments, newAttachmentUrl],
                                attachment: newAttachmentUrl // For backward compatibility
                              }));
                              setNewAttachmentUrl(''); // Clear input
                              setCurrentViewingAttachmentIndex(attachments.length); // View the new attachment
                            }
                          }}
                          disabled={!newAttachmentUrl}
                        >
                          Add
                        </Button>
                        <input
                          accept="image/*"
                          id="upload-screenshots-desktop"
                          type="file"
                          multiple  // Enable multiple file selection
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              handleMultipleFileUpload(e.target.files);
                            }
                          }}
                        />
                        <label htmlFor="upload-screenshots-desktop">
                          <Tooltip title="Select multiple files (hold Ctrl/Cmd)">
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<PhotoCameraIcon />}
                            >
                              Upload
                            </Button>
                          </Tooltip>
                        </label>
                      </Box>
                      
                      {/* Display current attachments */}
                      {currentTask?.attachments && currentTask.attachments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Current attachments ({currentTask.attachments.length}):
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {currentTask.attachments.map((attachment, index) => (
                              <Chip 
                                key={index}
                                label={attachment.split('/').pop().split('\\').pop()}
                                onClick={() => setCurrentViewingAttachmentIndex(index)}
                                onDelete={() => {
                                  // Remove this attachment
                                  const updatedAttachments = currentTask.attachments.filter((_, i) => i !== index);
                                  setCurrentTask(prev => ({
                                    ...prev,
                                    attachments: updatedAttachments,
                                    // Update single attachment field if removing the current one
                                    attachment: prev.attachment === attachment && updatedAttachments.length > 0
                                      ? updatedAttachments[0]
                                      : prev.attachment === attachment
                                        ? ''
                                        : prev.attachment
                                  }));
                                  // Reset viewing index if needed
                                  if (currentViewingAttachmentIndex >= updatedAttachments.length) {
                                    setCurrentViewingAttachmentIndex(Math.max(0, updatedAttachments.length - 1));
                                  }
                                }}
                                sx={{ 
                                  maxWidth: '150px',
                                  bgcolor: currentViewingAttachmentIndex === index ? 'primary.main' : 'default',
                                  color: currentViewingAttachmentIndex === index ? 'white' : 'inherit'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Right column - Attachment viewer */}
              {currentTask?.attachments && currentTask.attachments.length > 0 && (
                <Grid item xs={12} md={7}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: '600px',
                    border: '3px solid #2196f3',
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#2196f3',
                          textAlign: 'center',
                          fontWeight: 'bold'
                        }}
                      >
                        Attachment Viewer
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      flex: 1,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'auto',
                      position: 'relative',
                      minHeight: '500px'
                    }}>
                      <MultipleAttachmentsViewer 
                        attachments={currentTask.attachments}
                        currentIndex={currentViewingAttachmentIndex}
                        onChangeIndex={setCurrentViewingAttachmentIndex}
                      />
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            {/* Show Reopen button if task is Completed or has dateCompleted */}
            {(currentTask?.status === 'Completed' || currentTask?.dateCompleted) ? (
              <Button 
                onClick={handleReopenTask}
                variant="contained"
                color="warning"
                startIcon={<RefreshIcon />}
                disabled={!currentTask?.description}
              >
                Reopen Task
              </Button>
            ) : (
              <Button 
                onClick={async () => {
                  // Get the current username using the same approach as elsewhere in the component
                  // First try localStorage, then currentUser, then fallback options
                  let username = localStorage.getItem('username');
                  if (!username) {
                    username = currentUser?.username;
                  }
                  if (!username) {
                    const token = localStorage.getItem('token') || 'dummy-token';
                    const decodedToken = decodeJWT(token);
                    username = decodedToken?.username || 'user';
                  }
                  
                  // Create SQLite-compatible date format for storing in the database
                  const completionDate = formatSQLiteDate();
                  console.log('[DEBUG] Setting completion date explicitly to:', completionDate);
                  
                  // Create an updated task with completion data
                  const updatedTask = {
                    ...currentTask,
                    status: 'Completed',
                    completionUserId: username,
                    dateCompleted: completionDate
                  };
                  
                  console.log('[DEBUG] Updated task with completion data:', updatedTask);
                  
                  // Update the state with completed task
                  setCurrentTask(updatedTask);
                  
                  // Small timeout to ensure state is updated before saving
                  setTimeout(() => {
                    // Save the task and close the dialog in one step
                    handleSaveTask();
                  }, 50);
                }}
                variant="contained"
                color="success"
                startIcon={<EditIcon />}
                disabled={!currentTask?.description || currentTask?.status === 'Completed'}
              >
                Mark as Completed
              </Button>
            )}
            
            <Box>
              {/* Add Delete button - only show if editing an existing task */}
              {currentTask?.id && (
                <Button 
                  onClick={() => handleDeleteTask(currentTask.id)} 
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  sx={{ mr: 1 }}
                >
                  Delete
                </Button>
              )}
              <Button onClick={handleCloseDialog} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTask} 
                variant="contained"
                color="primary"
                disabled={!currentTask?.description}
              >
                Save
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TaskTracker;